import type { FeedItem, Selection } from '../types/feedContract'
import { getSelections, getSelectionSet, selectionSetIsSameGameParlay } from './selectionSet'
import { DEFAULT_TRANSLATOR, type Translator } from '../i18n/translator'

const PERSONALIZED_BET_LABEL_PATTERN = /personalized bets?/i

export interface CardEditorial {
  eyebrow: string
  title: string
  subtitle: string
  body: string
  personalized: boolean
}

function trimmed(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

function selectionLabel(selection: Selection): string {
  return trimmed(selection.label)
    || trimmed(selection.name)
    || trimmed(selection.selection?.name)
}

function summarizedSelections(selections: Selection[], i18n: Translator): string {
  const labels = selections.map(selectionLabel).filter(Boolean)
  if (labels.length === 0) return ''
  const visible = labels.slice(0, 2).join(' + ')
  const remaining = labels.length - 2
  return remaining > 0 ? i18n.t('editorial.more', { visible, remaining }) : visible
}

function fallbackSubtitle(item: FeedItem, selections: Selection[], title: string, i18n: Translator): string {
  const candidates: unknown[] = [item.text]
  if (selections.length === 1) {
    const selection = selections[0]
    candidates.push(selection.subtitle, selection.market?.name, selection.event?.name)
  }
  candidates.push(summarizedSelections(selections, i18n))

  const normalizedTitle = title.toLocaleLowerCase()
  return candidates
    .map(trimmed)
    .find((candidate) => candidate && candidate.toLocaleLowerCase() !== normalizedTitle) || ''
}

function fallbackTitle(item: FeedItem, selections: Selection[], i18n: Translator): string {
  if (selections.length === 1) {
    return selectionLabel(selections[0]) || i18n.t('editorial.onePick')
  }
  if (selectionSetIsSameGameParlay(getSelectionSet(item))) {
    return i18n.t('editorial.sameGame')
  }
  if (selections.length > 1) {
    return i18n.t('editorial.multiPick', { count: selections.length })
  }
  return i18n.t('editorial.pickedForYou')
}

/**
 * Resolves the customer-facing card hierarchy. Explicit Studio fields always
 * win; personalized cards receive deterministic fallbacks so historical
 * generated posts also render with a headline before they are reprojected.
 */
export function resolveCardEditorial(item: FeedItem, i18n: Translator = DEFAULT_TRANSLATOR): CardEditorial | null {
  const title = trimmed(item.title)
  const subtitle = trimmed(item.subtitle)
  const text = trimmed(item.text)
  const personalized = item.content_origin === 'personalized_ticket'
    || item.content_origin === 'personalized_recommendation'

  if (!personalized && !title && !subtitle) return null

  const selections = getSelections(getSelectionSet(item))
  const resolvedTitle = title || (personalized ? fallbackTitle(item, selections, i18n) : '')
  const resolvedSubtitle = subtitle || (personalized ? fallbackSubtitle(item, selections, resolvedTitle, i18n) : '')
  const body = personalized
    ? ''
    : text

  return {
    eyebrow: personalized && !PERSONALIZED_BET_LABEL_PATTERN.test(trimmed(item.creator?.display_name) || trimmed(item.creator?.name))
      ? i18n.t('editorial.personalizedBet')
      : '',
    title: resolvedTitle,
    subtitle: resolvedSubtitle,
    body,
    personalized,
  }
}
